/**
 * Generic Table Variants API Client
 * 
 * Provides CRUD operations for table view variants (filters, sorting, columns).
 * Works with any applicationId to support multiple tables/views.
 */

import { buildApiUrl } from "../lib/api";
import { getAuthHeaders } from "./auth";

export interface TableVariantConfiguration {
  filters?: Record<string, unknown>;
  sort?: {
    key: string;
    direction: "asc" | "desc";
  } | null;
  columns?: Array<{
    key: string;
    visible: boolean;
    width?: number | null;
  }>;
  pagination?: {
    pageSize: number;
  };
  [key: string]: unknown; // Allow application-specific extensions
}

export interface TableVariant {
  _id: string;
  type: "tableVariant";
  applicationId: string;
  userId: string;
  name: string;
  isDefault: boolean;
  configuration: TableVariantConfiguration;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTableVariantInput {
  applicationId: string;
  name: string;
  isDefault?: boolean;
  configuration: TableVariantConfiguration;
}

export interface UpdateTableVariantInput {
  name?: string;
  isDefault?: boolean;
  configuration?: TableVariantConfiguration;
}

/**
 * List all variants for an application
 */
export async function listTableVariants(
  applicationId: string
): Promise<TableVariant[]> {
  const url = buildApiUrl("/api/table-variants", { applicationId });

  const response = await fetch(url, {
    headers: getAuthHeaders(),
    credentials: "include"
  });

  if (!response.ok) {
    throw new Error(`Failed to list variants: ${response.status}`);
  }

  return response.json();
}

/**
 * Get the default variant for an application
 */
export async function getDefaultTableVariant(
  applicationId: string
): Promise<TableVariant | null> {
  const url = buildApiUrl("/api/table-variants/default", { applicationId });

  const response = await fetch(url, {
    headers: getAuthHeaders(),
    credentials: "include"
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to get default variant: ${response.status}`);
  }

  return response.json();
}

/**
 * Get a single variant by ID
 */
export async function getTableVariant(id: string): Promise<TableVariant> {
  const response = await fetch(buildApiUrl(`/api/table-variants/${id}`), {
    headers: getAuthHeaders(),
    credentials: "include"
  });

  if (!response.ok) {
    throw new Error(`Failed to get variant: ${response.status}`);
  }

  return response.json();
}

/**
 * Create a new variant
 * If isDefault=true, other defaults for the same applicationId are unset
 */
export async function createTableVariant(
  input: CreateTableVariantInput
): Promise<TableVariant> {
  const response = await fetch(buildApiUrl("/api/table-variants"), {
    method: "POST",
    credentials: "include",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Failed to create variant: ${response.status}`);
  }

  return response.json();
}

/**
 * Update an existing variant
 * If isDefault is set to true, other defaults are unset
 */
export async function updateTableVariant(
  id: string,
  input: UpdateTableVariantInput
): Promise<TableVariant> {
  const response = await fetch(buildApiUrl(`/api/table-variants/${id}`), {
    method: "PUT",
    credentials: "include",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Failed to update variant: ${response.status}`);
  }

  return response.json();
}

/**
 * Delete a variant
 */
export async function deleteTableVariant(id: string): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/table-variants/${id}`), {
    method: "DELETE",
    headers: getAuthHeaders(),
    credentials: "include"
  });

  if (!response.ok) {
    throw new Error(`Failed to delete variant: ${response.status}`);
  }
}
